import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import type { FollowUpActionTablePage } from 'src/types/myFollowUp';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useDownloadManager } from 'src/components/downloadManager/DownloadManagerContext';
import { useFollowUpActionListQuery } from 'src/components/myFollowUp/FollowUpActionPage/hooks/useFollowUpActionListQuery';
import { useMyFollowUpFilters } from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/hooks';

interface FollowUpActionTableValue {
  followUpActions: FollowUpActionTablePage;
  offset: number;
  setOffset: Dispatch<SetStateAction<number>>;
  ordering: string;
  setOrdering: Dispatch<SetStateAction<string>>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  pageSize: number;
  downloadAll: () => void;
}

const emptyAction = {
  count: 0,
  results: [],
  next: '',
  previous: '',
};

const FollowUpActionTableContext = createContext<FollowUpActionTableValue>({
  followUpActions: emptyAction,
  offset: 0,
  setOffset: () => {},
  ordering: '-id',
  setOrdering: () => {},
  loading: false,
  error: null,
  refetch: () => {},
  pageSize: 10,
  downloadAll: () => {},
});

interface FollowUpActionTableProviderProvider {
  pageSize?: number;
  children?: ReactNode;
}

export const FollowUpActionTableProvider: FC<
  FollowUpActionTableProviderProvider
> = ({ pageSize = 10, children }) => {
  const [offset, setOffset] = useState(0);
  const [ordering, setOrdering] = useState('-id');
  const {
    data,
    isFetching: loading,
    error,
    refetch,
  } = useFollowUpActionListQuery(offset, ordering, pageSize);
  const followUpActions = useMemo(() => (data ? data : emptyAction), [data]);
  const { dateRange, appliedFilters } = useMyFollowUpFilters();
  const { poll } = useDownloadManager();

  const downloadAll = () => {
    HttpService.get(apiPaths.FOLLOW_UP_ACTIONS.DOWNLOAD_ALL, {
      ...dateRange,
      ...appliedFilters,
    }).then(() => {
      poll();
    });
  };

  return (
    <FollowUpActionTableContext.Provider
      value={{
        followUpActions,
        offset,
        setOffset,
        ordering,
        setOrdering,
        loading,
        error,
        refetch,
        pageSize,
        downloadAll,
      }}
    >
      {children}
    </FollowUpActionTableContext.Provider>
  );
};

const useFollowUpActionTable = () => useContext(FollowUpActionTableContext);

export default useFollowUpActionTable;
